import { LitElement, html, css, unsafeCSS } from 'lit';
import base from '../styles/base.css?inline';
import { INVESTIGATION_TYPES, investigationSuggestions, buildReportOutline, flattenOutline } from '@mv/models';
import { api } from '../api.js';
import { auth } from '../auth.js';
import { reportUiStyles, SOURCE_TAGS } from '../utils/report-ui.js';

/** '3,3 ha' of '450 m²'. */
const areaLabel = (m2) => {
  if (!m2) return '';
  return m2 >= 10000 ? `${String(Math.round(m2 / 1000) / 10).replace('.', ',')} ha` : `${Math.round(m2)} m²`;
};

/**
 * report-setup-dialog — 'Nieuw rapport': welke onderzoeken, welke deellocaties, welk sjabloon
 * en welke titel (featureplan rapportage §1, stap 1 en 2).
 *
 * Bovenaan wat GeoVoice voorstelt, met de reden en de bron (veldwerkopdracht, boringen): de
 * adviseur moet kunnen zien waaróm grondwater aanstaat voordat hij het bevestigt. Zet hij een
 * voorstel uit, dan blijft het staan met de reden, zodat hij het terug kan zetten. Daaronder de
 * overige onderzoeken als knoppen. Onderaan hoeveel hoofdstukken, paragrafen en bijlagen het
 * rapport krijgt; dat schuift mee.
 *
 * @fires report-created  detail: { report }
 * @fires close
 * @fires open-map
 */
export class ReportSetupDialog extends LitElement {
  static properties = {
    projectId: { type: String },
    projectBorings: { type: Array },
    sublocations: { type: Array },
    /** Genormaliseerde veldwerkopdracht, of null. */
    fieldOrder: { type: Object },
    _types: { state: true },
    _sublocationIds: { state: true },
    _title: { state: true },
    _templates: { state: true },
    _templateId: { state: true },
    _busy: { state: true },
    _error: { state: true },
  };

  static styles = [css`${unsafeCSS(base)}`, reportUiStyles, css`
    :host { position:fixed; inset:0; z-index:var(--mv-z-modal, 1200); display:flex; align-items:center; justify-content:center;
            background:rgba(24,28,50,.45); backdrop-filter:blur(3px); padding:24px; }
    .dialog { width:100%; max-width:620px; max-height:calc(100vh - 48px); overflow-y:auto; background:var(--mv-surface); border-radius:var(--mv-radius-xl, 18px);
              box-shadow:0 24px 60px rgba(24,28,50,.18); display:flex; flex-direction:column; animation:in .2s ease-out; }
    @keyframes in { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:none; } }
    header { display:flex; align-items:flex-start; gap:12px; padding:22px 24px 4px; }
    header div { flex:1; display:flex; flex-direction:column; gap:4px; }
    h2 { margin:0; font-size:1.1875rem; font-weight:700; letter-spacing:-.02em; color:var(--mv-text); }
    header p { margin:0; font-size:.8125rem; color:var(--mv-text-sub); line-height:1.5; }
    .x { width:32px; height:32px; border:none; background:var(--mv-surface-alt); border-radius:8px; font-size:.95rem; color:var(--mv-text-sub); cursor:pointer; flex:none; }
    section { padding:16px 24px 0; display:flex; flex-direction:column; gap:8px; }

    .sugg { border:1px solid var(--mv-border); border-radius:12px; overflow:hidden; }
    .opt { width:100%; border:none; border-top:1px solid var(--mv-border-soft); background:var(--mv-surface); padding:12px 14px; display:grid;
           grid-template-columns:20px minmax(0,1fr) auto; gap:12px; align-items:start; text-align:left; cursor:pointer; font:inherit; color:inherit; }
    .opt:first-child { border-top:none; }
    .opt:hover { background:var(--mv-surface-alt); }
    .opt.on { background:var(--mv-primary-light); }
    .opt.fixed { cursor:default; }
    .opt .check { margin-top:1px; }
    .name { font-size:.875rem; font-weight:600; color:var(--mv-text); }
    .norm { font-weight:500; color:var(--mv-text-muted); font-size:.78rem; margin-left:4px; }
    .reason { font-size:.78rem; color:var(--mv-text-sub); line-height:1.45; margin-top:3px; }

    .others { display:flex; gap:8px; flex-wrap:wrap; }
    .chip { border:1px solid var(--mv-border); background:var(--mv-surface); border-radius:9px; padding:8px 12px; display:flex; align-items:center; gap:8px;
            cursor:pointer; font:inherit; color:inherit; }
    .chip.on { background:var(--mv-primary-light); border-color:var(--mv-primary); }
    .chip .check { width:16px; height:16px; font-size:10px; }
    .chip b { font-size:.8125rem; font-weight:600; color:var(--mv-text); }
    .chip span:last-child { font-size:.75rem; color:var(--mv-text-muted); }
    .hint { font-size:.75rem; color:var(--mv-text-sub); }

    .grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(min(100%, 240px), 1fr)); gap:14px; padding:16px 24px 0; }
    .grid > div { display:flex; flex-direction:column; gap:8px; }
    .dls { display:flex; gap:8px; flex-wrap:wrap; }
    .dl { border:1px solid var(--mv-border); background:var(--mv-surface); border-radius:9px; padding:7px 11px; display:flex; align-items:center; gap:8px;
          cursor:pointer; font:inherit; }
    .dl.on { border-color:#C2410C; background:#FFF8F3; }
    .dl .check { width:16px; height:16px; font-size:10px; }
    .dl.on .check { background:#C2410C; border-color:#C2410C; }
    .dl b { font-size:.8125rem; font-weight:700; color:#C2410C; }
    .dl span:last-child { font-size:.75rem; color:var(--mv-text-muted); }
    :host-context([data-theme='dark']) .dl.on { background:#3A2618; }
    :host-context([data-theme='dark']) .dl b { color:#F2A878; }
    select, input.title { border:1px solid var(--mv-border); border-radius:9px; padding:8px 10px; font:inherit; font-size:.8125rem; color:var(--mv-text);
                          background:var(--mv-surface-alt); width:100%; box-sizing:border-box; outline:none; }
    input.title:focus, select:focus { border-color:var(--mv-primary); background:var(--mv-surface); }
    .warn { margin:10px 24px 0; background:var(--mv-warning-panel-bg); border-radius:9px; padding:9px 12px; display:flex; gap:10px; align-items:center;
            flex-wrap:wrap; font-size:.78rem; color:var(--mv-warning-text); line-height:1.45; }
    .warn span { flex:1; min-width:220px; }
    .warn a, .link { font-weight:600; color:inherit; text-decoration:underline; cursor:pointer; }
    footer { display:flex; align-items:center; gap:10px; flex-wrap:wrap; padding:16px 24px 18px; border-top:1px solid var(--mv-border); margin-top:18px; }
    footer .sum { flex:1; min-width:200px; font-size:.78rem; color:var(--mv-text-sub); }
    .error { color:var(--mv-danger); font-size:.78rem; padding:10px 24px 0; }
  `];

  constructor() {
    super();
    this.projectBorings = [];
    this.sublocations = [];
    this.fieldOrder = null;
    this._types = null;
    this._sublocationIds = null;
    this._title = '';
    this._templates = [];
    this._templateId = '';
    this._busy = false;
    this._error = '';
    this._onKey = (e) => { if (e.key === 'Escape' && !this._busy) this._close(); };
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('keydown', this._onKey);
    // Sjablonen zijn optioneel: lukt het ophalen niet, dan gebruikt het rapport het standaardsjabloon.
    api.getReportTemplates().then((res) => {
      this._templates = res?.templates || [];
      this._templateId = this._templates.find((x) => x.isDefault)?.id || '';
    }).catch(() => {});
  }
  disconnectedCallback() { window.removeEventListener('keydown', this._onKey); super.disconnectedCallback(); }

  get _activeBorings() { return (this.projectBorings || []).filter((b) => !b.deletedAt); }

  _suggestions() {
    const borings = this._activeBorings;
    return investigationSuggestions({ borings, peilbuizen: borings.flatMap((b) => b.peilbuizen || []), fieldOrder: this.fieldOrder });
  }

  willUpdate(changed) {
    // De voorselectie één keer, zodra de boringen er zijn; daarna is het de keuze van de adviseur.
    if (this._types === null && (changed.has('projectBorings') || changed.has('fieldOrder'))) {
      this._types = new Set(this._suggestions().items.filter((i) => i.suggested).map((i) => i.key));
    }
    if (this._sublocationIds === null && changed.has('sublocations')) {
      this._sublocationIds = new Set((this.sublocations || []).map((s) => s.id));
    }
    if (!this._title && changed.has('fieldOrder')) {
      const name = this.fieldOrder?.project?.name || this.fieldOrder?.project?.address;
      if (name) this._title = `Verkennend bodemonderzoek ${name}`;
    }
  }

  _toggleType(key) {
    const next = new Set(this._types);
    if (next.has(key)) next.delete(key); else next.add(key);
    this._types = next;
  }

  _toggleSublocation(id) {
    const next = new Set(this._sublocationIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    this._sublocationIds = next;
  }

  _close() { this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true })); }

  async _create() {
    this._busy = true;
    this._error = '';
    try {
      const report = await api.createReport(this.projectId, {
        investigationTypes: [...this._types],
        sublocationIds: [...(this._sublocationIds || [])],
        title: this._title.trim() || undefined,
        templateId: this._templateId || undefined,
      });
      this.dispatchEvent(new CustomEvent('report-created', { detail: { report }, bubbles: true, composed: true }));
    } catch (e) {
      this._error = e.message || 'Aanmaken mislukt.';
    } finally {
      this._busy = false;
    }
  }

  render() {
    const types = this._types || new Set();
    const { items, hints } = this._suggestions();
    const typeOf = Object.fromEntries(INVESTIGATION_TYPES.map((t) => [t.key, t]));
    const suggested = items.filter((i) => i.suggested);
    const others = items.filter((i) => !i.suggested);
    const outline = buildReportOutline([...types]);
    const paragraphs = flattenOutline(outline.chapters).length;
    const sublocs = this.sublocations || [];
    const unassigned = this._activeBorings.filter((b) => !(b.sublocationIds || []).length);
    // Een tip over een onderzoek dat de adviseur al heeft aangezet, is geen tip meer.
    const shownHints = types.has('asbestosRubble') ? [] : hints;

    return html`
      <div class="dialog" role="dialog" aria-modal="true" aria-labelledby="t">
        <header>
          <div>
            <h2 id="t">Nieuw rapport</h2>
            <p>${suggested.length > 1 ? 'We hebben vooraf aangevinkt wat uit je project blijkt. Pas aan waar nodig.' : 'Kies welke onderzoeken in het rapport komen.'}</p>
          </div>
          <button class="x" title="Sluiten" @click=${() => this._close()}>✕</button>
        </header>

        <section>
          <span class="caps">Voorgesteld op basis van je project</span>
          <div class="sugg">
            ${suggested.map((i) => {
              const on = types.has(i.key);
              const tag = SOURCE_TAGS[i.source] || SOURCE_TAGS.standard;
              const reason = on || i.fixed ? i.reason : `Uitgezet. Voorgesteld omdat: ${i.reason.charAt(0).toLowerCase()}${i.reason.slice(1)}`;
              return html`
                <button class="opt ${on ? 'on' : ''} ${i.fixed ? 'fixed' : ''}" aria-pressed=${on ? 'true' : 'false'}
                        @click=${() => { if (!i.fixed) this._toggleType(i.key); }}>
                  <span class="check ${on ? 'on' : ''}">${on ? '✓' : ''}</span>
                  <span><span class="name">${typeOf[i.key].label}</span><span class="norm">${typeOf[i.key].norm}</span>
                    <div class="reason">${reason}</div></span>
                  <span class="tag ${tag.cls}">${tag.label}</span>
                </button>`;
            })}
          </div>
        </section>

        ${others.length ? html`
          <section>
            <span class="caps">Overige onderzoeken</span>
            <div class="others">
              ${others.map((i) => {
                const on = types.has(i.key);
                return html`
                  <button class="chip ${on ? 'on' : ''}" aria-pressed=${on ? 'true' : 'false'} @click=${() => this._toggleType(i.key)}>
                    <span class="check ${on ? 'on' : ''}">${on ? '✓' : ''}</span><b>${typeOf[i.key].label}</b><span>${typeOf[i.key].norm}</span>
                  </button>`;
              })}
            </div>
            ${shownHints.map((h) => html`<span class="hint">Tip: ${h}</span>`)}
          </section>` : ''}

        <div class="grid">
          <div>
            <span class="caps">Deellocaties</span>
            ${sublocs.length ? html`
              <div class="dls">
                ${sublocs.map((s) => {
                  const on = !!this._sublocationIds?.has(s.id);
                  return html`
                    <button class="dl ${on ? 'on' : ''}" aria-pressed=${on ? 'true' : 'false'} @click=${() => this._toggleSublocation(s.id)}>
                      <span class="check ${on ? 'on' : ''}">${on ? '✓' : ''}</span><b>${s.name}</b><span>${areaLabel(s.areaM2)}</span>
                    </button>`;
                })}
              </div>`
            : html`<span class="hint">Nog geen deellocaties. Teken ze in het <a class="link" @click=${() => this._toMap()}>monsterplan</a>; het rapport deelt alles per deellocatie in.</span>`}
          </div>
          <div>
            <span class="caps">Sjabloon</span>
            <select .value=${this._templateId} @change=${this._kiesSjabloon}>
              ${this._templates.length ? this._templates.map((x) => html`<option value=${x.id} ?selected=${x.id === this._templateId}>${x.name}${x.isDefault ? ' (standaard)' : ''}</option>`)
                : html`<option value="">GeoVoice standaard</option>`}
              ${this._kanSjablonenBeheren ? html`<option value="__new__">➕ Maak nieuwe sjabloon aan</option>` : ''}
            </select>
          </div>
        </div>
        ${sublocs.length && unassigned.length ? html`
          <div class="warn">
            <span>${unassigned.length === 1 ? '1 boring hoort' : `${unassigned.length} boringen horen`} nog bij geen deellocatie en
              ${unassigned.length === 1 ? 'komt' : 'komen'} niet in de tabellen.</span>
            <a @click=${() => this._toMap()}>Toewijzen op kaart</a>
          </div>` : ''}

        <section>
          <span class="caps">Titel</span>
          <input class="title" .value=${this._title} maxlength="200" placeholder="Verkennend bodemonderzoek …"
                 @input=${(e) => { this._title = e.target.value; }}>
        </section>

        ${this._error ? html`<div class="error">${this._error}</div>` : ''}
        <footer>
          <span class="sum">${types.size
            ? `${outline.chapters.length} hoofdstukken · ${paragraphs} paragrafen · ${outline.appendices.length} bijlagen`
            : 'Kies minstens één onderzoek.'}</span>
          <button class="btn ghost" ?disabled=${this._busy} @click=${() => this._close()}>Annuleren</button>
          <button class="btn primary" ?disabled=${this._busy || !types.size} @click=${() => this._create()}>
            ${this._busy ? 'Aanmaken…' : 'Rapport aanmaken'}</button>
        </footer>
      </div>`;
  }

  /**
   * Dezelfde regel als bij 'Nieuw project' en de instellingenpagina: sjablonen beheren mag de
   * eigenaar of beheerder van het bedrijf, en een systeembeheerder.
   */
  get _kanSjablonenBeheren() {
    const u = auth.getUser();
    return !!u && (u.is_system_admin || u.company_role === 'owner' || u.company_role === 'manager');
  }

  /** 'Maak nieuwe sjabloon aan' is geen sjabloon: dan naar de rapportage-instellingen. */
  _kiesSjabloon(e) {
    if (e.target.value !== '__new__') { this._templateId = e.target.value; return; }
    e.target.value = this._templateId;
    this._close();
    window.history.pushState({}, '', '/settings/report');
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  _toMap() {
    this.dispatchEvent(new CustomEvent('open-map', { bubbles: true, composed: true }));
    this._close();
  }
}

customElements.define('report-setup-dialog', ReportSetupDialog);
